/**
 * Attachments that could not be shown, one card per map (the map the file
 * belongs to): the map's name and ID, then each file's name (as written in
 * the note: a file that can't be decrypted has no readable stored name) and
 * ID, with the reason. A file is listed once per card; one dismissed is not
 * reported again until the next start (a redrawn note tries it again).
 */
import { progressRegion } from './operation-progress.js';
import { VaultStorage } from '../../../engine/crypto/vault-storage.js';
import { t } from '../../../shared/i18n/index.js';

const dismissed = new Set();

/** The reason, in words. */
function reasonOf(error) {
  if (error?.code === 'no-key') return t('vault.attachment.failures.noKey');
  if (error?.name === 'OperationError') return t('vault.attachment.failures.keyMismatch');
  // The server says 403 both for "no such file in this map" and "no access" (so files can't be probed).
  if (error?.status === 403) return t('vault.attachment.failures.refused');
  if (error?.status === 404) return t('vault.attachment.failures.notFound');
  return error?.message || String(error || t('vault.progress.retry'));
}

function mapTitle(docId) {
  const entry = VaultStorage.getIndex?.()?.docs?.find((doc) => doc.docId === docId);
  if (!entry) return t('vault.attachment.failures.unknownMap');
  return t('vault.attachment.failures.map', { name: entry.title || t('common.untitled') });
}

function idLine(id) {
  const code = document.createElement('code');
  code.className = 'attachment-failure-id';
  code.textContent = id;
  return code;
}

function cardFor(docId) {
  const region = progressRegion();
  const existing = [...region.querySelectorAll('.attachment-failures')].find((card) => card.dataset.docId === docId);
  if (existing) return existing;
  const card = document.createElement('div');
  card.className = 'operation-progress-card attachment-failures';
  card.dataset.state = 'error';
  card.dataset.docId = docId;
  card.setAttribute('role', 'alert');
  const heading = document.createElement('strong');
  heading.textContent = t('vault.attachment.failures.title');
  const map = document.createElement('div');
  map.className = 'attachment-failure-map';
  map.append(mapTitle(docId), ' ', idLine(docId));
  const list = document.createElement('ul');
  list.className = 'attachment-failure-list';
  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = t('vault.progress.dismiss');
  close.addEventListener('click', () => {
    list.querySelectorAll('li').forEach((item) => dismissed.add(`${docId}/${item.dataset.attachmentId}`));
    card.remove();
    if (!region.children.length) region.remove();
  });
  card.append(heading, map, list, close);
  region.prepend(card);
  return card;
}

/**
 * @param {{docId: string, attachmentId: string, name?: string, error: any}} failure
 *   docId: the map the file belongs to (from its reference)
 */
export function reportAttachmentFailure({ docId, attachmentId, name, error }) {
  if (typeof document === 'undefined' || !document.body?.appendChild) return;
  if (dismissed.has(`${docId}/${attachmentId}`)) return;
  const list = cardFor(docId).querySelector('.attachment-failure-list');
  if ([...list.children].some((item) => item.dataset.attachmentId === attachmentId)) return;
  const item = document.createElement('li');
  item.dataset.attachmentId = attachmentId;
  const title = document.createElement('span');
  title.textContent = name ? t('vault.attachment.failures.file', { name }) : t('vault.attachment.failures.unnamed');
  const reason = document.createElement('span');
  reason.className = 'operation-progress-detail';
  reason.textContent = reasonOf(error);
  item.append(title, ' ', idLine(attachmentId), reason);
  list.append(item);
}
